import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActionArea,
  Button,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ScreenshotIcon from '@mui/icons-material/Screenshot';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import ExtensionIcon from '@mui/icons-material/Extension';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { withTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import ImportDetailDialog from './ImportDetailDialog';
import CSVImportDialog from './CSVImportDialog';
import {
  useListPreferences,
  SortableHeader,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';

const STORAGE_KEY = 'imports_list_preferences';

const defaultPreferences = {
  sortField: 'created_at',
  sortDirection: 'desc',
  filters: {
    sourceType: 'all', // all, email, extension, file_upload, url_parse
    documentType: 'all', // all, lasku, kuitti, lahete, etc.
  },
  searchQuery: '',
};

// Document type config with translation keys
const documentTypes = [
  { value: 'lasku', labelKey: 'Invoice', icon: DescriptionIcon, color: 'primary' },
  { value: 'kuitti', labelKey: 'Receipt', icon: ReceiptIcon, color: 'success' },
  { value: 'lahete', labelKey: 'Delivery note', icon: LocalShippingIcon, color: 'info' },
  { value: 'hinnasto', labelKey: 'Price list', icon: AttachMoneyIcon, color: 'warning' },
  { value: 'tuoteluettelo', labelKey: 'Product catalog', icon: MenuBookIcon, color: 'secondary' },
  { value: 'tarjous', labelKey: 'Quote', icon: RequestQuoteIcon, color: 'info' },
  { value: 'kuvakaappaus', labelKey: 'Screenshot', icon: ScreenshotIcon, color: 'default' },
  { value: 'muu', labelKey: 'Other', icon: HelpOutlineIcon, color: 'default' },
];

// Source type config with translation keys
const sourceTypes = [
  { value: 'email', labelKey: 'Email', icon: EmailIcon, color: 'info' },
  { value: 'extension', labelKey: 'Extension', icon: ExtensionIcon, color: 'secondary' },
  { value: 'file_upload', labelKey: 'File upload', icon: UploadFileIcon, color: 'success' },
  { value: 'url_parse', labelKey: 'URL', icon: LinkIcon, color: 'primary' },
  { value: 'csv_import', labelKey: 'CSV Import', icon: TableChartIcon, color: 'warning' },
  { value: 'ics_import', labelKey: 'ICS Import', icon: CalendarTodayIcon, color: 'info' },
];

const ImportsListView = ({ t }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);

  const {
    preferences,
    updatePreferences,
    updateFilters,
    resetFilters,
    handleSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  const fetchImports = useCallback(async () => {
    try {
      setLoading(true);
      const orgId = localStorage.getItem('currentOrganizationId');
      const params = new URLSearchParams();
      params.append('organization', orgId);

      if (preferences.searchQuery) params.append('search', preferences.searchQuery);
      if (preferences.filters.sourceType !== 'all') params.append('source_type', preferences.filters.sourceType);
      if (preferences.filters.documentType !== 'all') params.append('document_type', preferences.filters.documentType);

      const response = await axios.get(`/api/imports/?${params.toString()}`);
      setImports(response.data.results || response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch imports:', err);
      setError(t('Failed to load imports'));
    } finally {
      setLoading(false);
    }
  }, [preferences.searchQuery, preferences.filters.sourceType, preferences.filters.documentType, t]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (preferences.filters.sourceType !== 'all') count++;
    if (preferences.filters.documentType !== 'all') count++;
    return count;
  }, [preferences.filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];

    if (preferences.filters.sourceType !== 'all') {
      const source = sourceTypes.find((s) => s.value === preferences.filters.sourceType);
      if (source) chips.push({ key: 'source', label: t(source.labelKey) });
    }

    if (preferences.filters.documentType !== 'all') {
      const docType = documentTypes.find((d) => d.value === preferences.filters.documentType);
      if (docType) chips.push({ key: 'docType', label: t(docType.labelKey) });
    }

    return chips;
  }, [preferences.filters, t]);

  const handleOpenDetail = (importItem) => {
    setSelectedImport(importItem);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedImport(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fi-FI', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDocumentTypeChip = (docType) => {
    const config = documentTypes.find((d) => d.value === docType) || documentTypes.find((d) => d.value === 'muu');
    const Icon = config.icon;
    return (
      <Chip
        icon={<Icon fontSize="small" />}
        label={t(config.labelKey)}
        size="small"
        color={config.color}
        variant="outlined"
      />
    );
  };

  const getSourceTypeChip = (sourceType) => {
    const config = sourceTypes.find((s) => s.value === sourceType) || { icon: HelpOutlineIcon, color: 'default', labelKey: sourceType };
    const Icon = config.icon;
    return (
      <Chip
        icon={<Icon fontSize="small" />}
        label={t(config.labelKey)}
        size="small"
        color={config.color}
        variant="filled"
      />
    );
  };

  const getImportTitle = (item) => {
    if (item.original_filename) return item.original_filename;
    if (item.subject) return item.subject;
    if (item.source_title) return item.source_title;
    if (item.source_url) return item.source_url.substring(0, 50) + '...';
    return t('Import') + ' #' + item.id;
  };

  // Mobile card view
  const renderMobileCard = (item) => (
    <Card key={item.id} sx={{ mb: 2 }}>
      <CardActionArea onClick={() => handleOpenDetail(item)}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            {getSourceTypeChip(item.source_type)}
            {item.document_type && getDocumentTypeChip(item.document_type)}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
            {getImportTitle(item)}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {formatDate(item.created_at)}
            </Typography>
            {item.parts_count > 0 && (
              <Chip
                icon={<InventoryIcon fontSize="small" />}
                label={`${item.parts_count} ${t('parts')}`}
                size="small"
                variant="outlined"
              />
            )}
            {item.is_csv_import && item.imported_count > 0 && (
              <Chip
                label={`${item.imported_count} ${t('imported')}`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );

  // Desktop table view
  const renderDesktopTable = () => (
    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: '12px' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'background.default' }}>
            <TableCell>
              <SortableHeader
                label={t('Source')}
                field="source_type"
                currentSort={preferences.sortField}
                currentDirection={preferences.sortDirection}
                onSort={handleSort}
              />
            </TableCell>
            <TableCell>
              <SortableHeader
                label={t('Document')}
                field="document_type"
                currentSort={preferences.sortField}
                currentDirection={preferences.sortDirection}
                onSort={handleSort}
              />
            </TableCell>
            <TableCell>{t('Name')}</TableCell>
            <TableCell>{t('Parts')}</TableCell>
            <TableCell>
              <SortableHeader
                label={t('Date')}
                field="created_at"
                currentSort={preferences.sortField}
                currentDirection={preferences.sortDirection}
                onSort={handleSort}
              />
            </TableCell>
            <TableCell align="right">{t('Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {imports.map((item) => (
            <TableRow
              key={item.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => handleOpenDetail(item)}
            >
              <TableCell>{getSourceTypeChip(item.source_type)}</TableCell>
              <TableCell>
                {item.document_type ? getDocumentTypeChip(item.document_type) : '-'}
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ maxWidth: 300 }} noWrap>
                  {getImportTitle(item)}
                </Typography>
              </TableCell>
              <TableCell>
                {item.is_csv_import ? (
                  <Chip
                    label={`${item.imported_count} ${t('imported')}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : item.parts_count > 0 ? (
                  <Chip
                    icon={<InventoryIcon fontSize="small" />}
                    label={item.parts_count}
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(item.created_at)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Tooltip title={t('View details')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenDetail(item);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t('Imports')}
        </Typography>
        {/* CSV is the only import offered here. The ICS calendar import was removed 2026-11-02:
            it never produced usable work orders and only raised support questions. */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCsvImportDialogOpen(true)}
        >
          {t('Import')}
        </Button>
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('Search imports...')}
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={imports.length}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('Source type')}>
          <FilterChip
            label={t('All')}
            active={preferences.filters.sourceType === 'all'}
            onClick={() => updateFilters({ sourceType: 'all' })}
            color="primary"
          />
          {sourceTypes.map((source) => (
            <FilterChip
              key={source.value}
              icon={React.createElement(source.icon)}
              label={t(source.labelKey)}
              active={preferences.filters.sourceType === source.value}
              onClick={() => updateFilters({ sourceType: preferences.filters.sourceType === source.value ? 'all' : source.value })}
              color={source.color}
            />
          ))}
        </FilterSection>

        <FilterSection title={t('Document type')} dividerTop>
          <FilterChip
            label={t('All')}
            active={preferences.filters.documentType === 'all'}
            onClick={() => updateFilters({ documentType: 'all' })}
            color="primary"
          />
          {documentTypes.map((docType) => (
            <FilterChip
              key={docType.value}
              icon={React.createElement(docType.icon)}
              label={t(docType.labelKey)}
              active={preferences.filters.documentType === docType.value}
              onClick={() => updateFilters({ documentType: preferences.filters.documentType === docType.value ? 'all' : docType.value })}
              color={docType.color}
            />
          ))}
        </FilterSection>
      </FilterPanel>

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading ? (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : imports.length === 0 ? (
        /* Empty state */
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: '12px' }}>
          <UploadFileIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {preferences.searchQuery || activeFilterCount > 0
              ? t('No imports match your criteria')
              : t('No imports yet')
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {preferences.searchQuery || activeFilterCount > 0
              ? t('Try adjusting your search or filters')
              : t('Import documents to see them here')
            }
          </Typography>
        </Paper>
      ) : (
        /* Content */
        isMobile ? (
          <Box>
            {imports.map(renderMobileCard)}
          </Box>
        ) : (
          renderDesktopTable()
        )
      )}

      {/* Detail Dialog */}
      <ImportDetailDialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        importData={selectedImport}
      />

      {/* CSV Import Dialog */}
      <CSVImportDialog
        open={csvImportDialogOpen}
        onClose={() => setCsvImportDialogOpen(false)}
        onImportComplete={() => {
          setCsvImportDialogOpen(false);
          fetchImports();
        }}
      />
    </Box>
  );
};

export default withTranslation()(ImportsListView);
